import { CaretLeftIcon, CaretRightIcon, DotsThreeIcon } from "@phosphor-icons/react/dist/ssr";
import type { ComponentPropsWithRef } from "react";
import * as React from "react";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button, type ButtonProps } from "./button";

const Pagination = ({ className, ...props }: React.ComponentProps<"nav">) => (
  <nav aria-label="pagination" className={cn("mx-auto flex w-full justify-center", className)} {...props} />
);
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef<HTMLUListElement, React.ComponentProps<"ul">>(
  ({ className, ...props }, ref) => (
    <ul ref={ref} className={cn("flex flex-row items-center gap-1", className)} {...props} />
  ),
);
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef<HTMLLIElement, React.ComponentProps<"li">>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn("", className)} {...props} />
));
PaginationItem.displayName = "PaginationItem";

type PaginationLinkProps = {
  isActive?: boolean;
  linkProps?: ComponentPropsWithRef<"a">;
} & ButtonProps;

const PaginationLink = ({ className, isActive, size = "icon", linkProps, children, ...props }: PaginationLinkProps) => (
  <Button
    variant={isActive ? "primary" : "ghost"}
    size={size}
    className={cn(className, "flex items-center")}
    {...props}
    asChild
  >
    {linkProps?.href ? (
      <a aria-current={isActive ? "page" : undefined} {...linkProps} href={linkProps?.href ?? ""}>
        {children}
      </a>
    ) : (
      children
    )}
  </Button>
);
PaginationLink.displayName = "PaginationLink";

const PaginationPrevious = ({ className, children, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink aria-label="Go to previous page" size="default" className={cn("gap-1 pl-2.5", className)} {...props}>
    {children || (
      <>
        <CaretLeftIcon className="h-4 w-4" />
        {/* <span className="hidden sm:inline">Poprzednia</span> */}
      </>
    )}
  </PaginationLink>
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = ({ className, children, ...props }: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink aria-label="Go to next page" size="default" className={cn("gap-1 pr-2.5", className)} {...props}>
    {children || (
      <>
        {/* <span className="hidden sm:inline">Następna</span> */}
        <CaretRightIcon className="h-4 w-4" />
      </>
    )}
  </PaginationLink>
);
PaginationNext.displayName = "PaginationNext";

const PaginationEllipsis = ({ className, ...props }: React.ComponentProps<"span">) => (
  <span aria-hidden className={cn("hidden h-9 w-9 items-center justify-center sm:flex", className)} {...props}>
    <DotsThreeIcon className="h-4 w-4" />
    <span className="sr-only">Więcej stron</span>
  </span>
);
PaginationEllipsis.displayName = "PaginationEllipsis";

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
